import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import * as path from 'path';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import * as url from 'url';
import { Constants, MixpanelService, SessionStorageService } from '../_services';
import { PdfjsViewerComponent } from './pdfjs-viewer.component';

GlobalWorkerOptions.workerSrc = url.resolve(Constants.BasePathAssets, 'js/pdfjs-dist/pdf.worker.min.js');

@Component({
  template: `
<app-pdfjs-viewer
  [source]="pdfSource"
  [languageCode]="currentLanguageCode"
  componentDivClass="container"
  containerDivClass="row"
  viewerDivClass="col"
  toolbarButtonClass="btn btn-sm btn-teal"
  (documentLoadComplete)="resetContainerDivHeight()"
>
</app-pdfjs-viewer>`,
  styleUrls: ['pdf-viewer-page.component.scss']
})
export class PdfViewerPageComponent implements AfterViewInit, OnInit, OnDestroy {
  /**
   * @access private
   */
  readonly pdfSource: string | null;

  /**
   * @access private
   */
  readonly currentLanguageCode: string;

  @ViewChild(PdfjsViewerComponent)
  private pdfjsViewerComponent: PdfjsViewerComponent;

  constructor(
    router: Router,
    route: ActivatedRoute,
    sessionState: SessionStorageService,
    private titleService: Title,
    private mixpanelService: MixpanelService
  ) {
    this.currentLanguageCode = sessionState.getCurrentLanguageCode();

    const routeSnapshot = route.snapshot;
    const urlPath = routeSnapshot.url.map((urlSegment) => urlSegment.path);
    urlPath.push(this.currentLanguageCode);

    // for a given URL segment, the last three elements is all we care about.
    // eg. if urlPath contains [ 'member', 'dash', 'conditions', 'total', 'en' ], it
    // would be splice()'d so that end result becomes [ 'conditions', 'total', 'en' ]
    if (urlPath.length > 3) {
      urlPath.splice(0, urlPath.length - 3);
    }

    const index = Constants.PdfSourceIndexMap.indexOf(urlPath);
    if (index >= 0 && index < Constants.PdfSource.length) {
      let pdfSourceUrl = Constants.PdfSource[index][0];
      try {
        const { viewportWidth } = this.viewportSize;
        if (viewportWidth < 1024) {
          pdfSourceUrl = Constants.PdfSource[index][1];
        }
      } catch (e) {
        /* ignore */
      }
      this.pdfSource = pdfSourceUrl;
    } else {
      this.pdfSource = null;
    }

    if (typeof this.pdfSource !== 'string') {
      router.navigate([]); // route to HTTP 404 page
      return;
    }

    this.resetContainerDivHeight = this.resetContainerDivHeight.bind(this);
  }

  ngOnInit(): void {
    // TODO pull document title from APIs instead of hard-coding it here
    const documentTitle =
      unescape(path.basename(this.pdfSource)) +
      (this.currentLanguageCode === Constants.LanguageCodeFrench ? ' - Vigie Domicile' : ' - Care For My Home');

    this.mixpanelService.trackPageView(documentTitle);
    this.titleService.setTitle(documentTitle);

    Array.from(document.getElementsByTagName('header'))[0].classList.add('pdf-viewer-page');
    Array.from(document.getElementsByTagName('main'))[0].classList.add('pdf-viewer-page');
    window.addEventListener('resize', this.resetContainerDivHeight);
  }

  ngAfterViewInit(): void {
    this.resetContainerDivHeight();
  }

  ngOnDestroy(): void {
    Array.from(document.getElementsByTagName('header'))[0].classList.remove('pdf-viewer-page');
    Array.from(document.getElementsByTagName('main'))[0].classList.remove('pdf-viewer-page');
    window.removeEventListener('resize', this.resetContainerDivHeight);
  }

  /**
   * @access protected
   */
  resetContainerDivHeight(): void {
    if (window && document && document.documentElement && document.querySelector) {
      const header = document.querySelector('header');
      const footer = document.querySelector('footer');
      const toolbar = document.querySelector(`.${this.pdfjsViewerComponent.toolbarClass}`);
      const containerDiv = document.getElementById(this.pdfjsViewerComponent.containerDivId);

      const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
      const headerHeight = header ? header.offsetHeight : 0;
      const footerHeight = footer ? footer.offsetHeight : 0;
      const toolbarHeight = toolbar ? (toolbar as HTMLElement).offsetHeight : 0;
      const extra = 0; // header's bottom margin

      if (containerDiv) {
        containerDiv.style.height = viewportHeight - headerHeight - toolbarHeight - footerHeight - extra + 'px';
      }
    }
  }

  protected get viewportSize(): { viewportWidth: number; viewportHeight: number } {
    let el: Window | HTMLElement = window;
    let prefix = 'inner';
    if (!('innerWidth' in window)) {
      el = document.documentElement || document.body;
      prefix = 'client';
    }
    return { viewportWidth: el[`${prefix}Width`], viewportHeight: el[`${prefix}Height`] };
  }
}



// WEBPACK FOOTER //
// ./src/app/global/pdf-viewer-page.component.ts